import { useAPPostings } from '@api/hooks/useAPPostings';
import { Card, CardContent, CardHeader, CardTitle } from '@components/common/Card';
import { AlertCircle, TrendingDown } from 'lucide-react';
import { format, differenceInDays, isBefore } from 'date-fns';

export const UpcomingDiscountDeadlines = () => {
  const { data: postings, isLoading } = useAPPostings('pending_approval');

  if (isLoading) {
    return null;
  }

  // Filter postings with upcoming discount deadlines
  // Ensure postings is an array (handle API failures gracefully)
  const postingsArray = Array.isArray(postings) ? postings : [];
  const upcomingDiscounts = postingsArray
    .filter(
      (p) =>
        p.discount_info?.has_discount &&
        p.discount_info?.discount_deadline &&
        p.status === 'pending_approval'
    )
    .map((p) => ({
      id: p.id,
      invoiceNum: p.invoice_num,
      vendor: p.vendor_name || p.vendor_name_extracted || 'Unknown Vendor',
      discountPercent: p.discount_info!.discount_percent,
      discountDeadline: p.discount_info!.discount_deadline!,
      daysUntilDeadline: differenceInDays(
        new Date(p.discount_info!.discount_deadline!),
        new Date()
      ),
    }))
    .filter((d) => d.daysUntilDeadline >= -1 && d.daysUntilDeadline <= 14) // Show if within 2 weeks or already past
    .sort((a, b) => a.daysUntilDeadline - b.daysUntilDeadline);

  if (upcomingDiscounts.length === 0) {
    return null;
  }

  const criticalCount = upcomingDiscounts.filter((d) => d.daysUntilDeadline <= 2).length;
  const urgentCount = upcomingDiscounts.filter(
    (d) => d.daysUntilDeadline > 2 && d.daysUntilDeadline <= 7
  ).length;

  return (
    <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
      <CardHeader>
        <div className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <CardTitle className="text-amber-900 dark:text-amber-100">
            Early Payment Discount Opportunities
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {criticalCount > 0 && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold text-red-800 dark:text-red-200">
                  {criticalCount} deadline(s) in next 2 days!
                </p>
                <p className="text-sm text-red-700 dark:text-red-300">
                  Take action now to avoid missing these discounts.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {upcomingDiscounts.map((discount) => {
            const isPast = discount.daysUntilDeadline < 0;
            const isCritical = discount.daysUntilDeadline <= 2;
            const isUrgent = discount.daysUntilDeadline > 2 && discount.daysUntilDeadline <= 7;

            return (
              <div
                key={discount.id}
                className={`p-3 rounded-lg border transition-colors ${
                  isPast
                    ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    : isCritical
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                    : isUrgent
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p
                      className={`font-semibold truncate ${
                        isPast
                          ? 'text-gray-800 dark:text-gray-300'
                          : isCritical
                          ? 'text-red-800 dark:text-red-200'
                          : isUrgent
                          ? 'text-yellow-800 dark:text-yellow-200'
                          : 'text-blue-800 dark:text-blue-200'
                      }`}
                    >
                      {discount.invoiceNum} • {discount.vendor}
                    </p>
                    <p
                      className={`text-sm truncate ${
                        isPast
                          ? 'text-gray-600 dark:text-gray-400'
                          : isCritical
                          ? 'text-red-700 dark:text-red-300'
                          : isUrgent
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      {discount.discountPercent}% discount
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p
                      className={`font-bold text-sm ${
                        isPast
                          ? 'text-gray-700 dark:text-gray-300'
                          : isCritical
                          ? 'text-red-700 dark:text-red-300'
                          : isUrgent
                          ? 'text-yellow-700 dark:text-yellow-300'
                          : 'text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      {isPast
                        ? 'Expired'
                        : discount.daysUntilDeadline === 0
                        ? 'Today!'
                        : discount.daysUntilDeadline === 1
                        ? 'Tomorrow'
                        : `${discount.daysUntilDeadline}d left`}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {format(new Date(discount.discountDeadline), 'MMM dd')}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-gray-600 dark:text-gray-400 pt-2 border-t border-amber-200 dark:border-amber-800">
          Showing {upcomingDiscounts.length} discount opportunity
          {upcomingDiscounts.length !== 1 ? 'ies' : ''} within 2 weeks.{' '}
          <a
            href="/records?type=documents&document_type=invoice"
            className="text-amber-700 dark:text-amber-300 font-semibold hover:underline"
          >
            Review in Documents →
          </a>
        </p>
      </CardContent>
    </Card>
  );
};
